/**
 * Archivos para el cliente: descargar y compartir (comprobantes, extractos). Sin dependencias.
 */

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

/** Nombre del archivo desde `Content-Disposition` (la API lo expone por CORS) o el de respaldo. */
export function filenameFrom(response: Response, fallback: string): string {
  const header = response.headers.get('Content-Disposition') ?? '';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (utf8) {
    try {
      return decodeURIComponent(utf8);
    } catch {
      // Cae al nombre simple.
    }
  }
  return /filename="?([^";]+)"?/i.exec(header)?.[1] ?? fallback;
}

/** Guarda un archivo en el dispositivo (enlace temporal con `download`). */
export function saveFile({ blob, filename }: DownloadedFile): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
}

/** ¿El navegador puede compartir este archivo con la hoja nativa (móvil)? */
export function canShareFile(file: File): boolean {
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') {
    return false;
  }
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

export type ShareOutcome = 'shared' | 'downloaded' | 'cancelled';

/**
 * Comparte el archivo con la hoja nativa (WhatsApp, correo…) o, si no se puede, lo descarga.
 * Llamar dentro del gesto del usuario: Safari exige activación para `navigator.share`.
 */
export async function shareOrDownload(
  file: File,
  data: { title: string; text?: string },
): Promise<ShareOutcome> {
  if (canShareFile(file)) {
    try {
      await navigator.share({
        files: [file],
        title: data.title,
        ...(data.text && { text: data.text }),
      });
      return 'shared';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
      // Sin activación de usuario u otro bloqueo: descargar.
    }
  }
  saveFile({ blob: file, filename: file.name });
  return 'downloaded';
}
